import { Component, ContentChildren, Input, OnInit, QueryList } from '@angular/core';
import { CustomColumnSettings } from '@app/datatables/models/custom-column-settings';
import { FilterInputComponent } from '@app/shared/components/filter-input/filter-input.component';
import { DatatablePage } from '@app/shared/datatable-page';
import { DataTableDirective } from '@app/shared/directives/data-table.directive';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type VisibilityMap = Record<string, boolean>;

@UntilDestroy()
@Component({
  selector: 'portal-datatable-optional-column-dropdown',
  templateUrl: './datatable-optional-column-dropdown.component.html',
  styles: [],
  standalone: false,
})
export class DatatableOptionalColumnDropdownComponent implements OnInit {
  @Input()
  filter?: FilterInputComponent<any>;

  @ContentChildren(TranscludeDirective)
  transclusions: QueryList<TranscludeDirective>;

  optionalColumns: CustomColumnSettings[] = [];

  private page: DatatablePage<any> | null = null;
  private storageId: string = 'optional-columns';
  private visibilityMap: VisibilityMap = {};

  constructor(
    private preferenceStorageService: PreferenceStorageService,
    private tableDirective: DataTableDirective
  ) {}

  ngOnInit(): void {
    this.tableDirective.page$.pipe(untilDestroyed(this)).subscribe((page) => {
      this.page = page;

      // Angular will throw an ExpressionChangedAfterItHasBeenChecked error if the data table page
      // is created in the ngAfterViewInit callback. By requesting an animation frame we trigger the
      // change detection after the code is executed.
      requestAnimationFrame(() => {
        this.optionalColumns = page.optionalColumns;

        this.preferenceStorageService
          .preference$<VisibilityMap>(this.storageId, {})
          .pipe(untilDestroyed(this))
          .subscribe((visibilityMap) => this.updateFromStorage(visibilityMap));
      });
    });

    if (this.filter != null) {
      this.filter.valueChanged.pipe(untilDestroyed(this)).subscribe(() => {
        if (this.page?.loading === false) {
          const activeFilters = this.filter.metaList.properties.filter(
            (property) => property.activeFilters > 0
          );

          const visibilityMap = {
            ...this.visibilityMap,
          };

          for (const filter of activeFilters) {
            visibilityMap[filter.key] = true;
          }

          this.updateVisibilityMap(visibilityMap);
        }
      });
    }
  }

  toggleColumn(name: string): void {
    this.updateVisibilityMap({
      ...this.visibilityMap,
      [name]: !this.visibilityMap[name],
    });
  }

  private updateFromStorage(visibilityMap: VisibilityMap): void {
    for (const column of this.optionalColumns) {
      this.visibilityMap[column.name] = visibilityMap[column.name] ?? column.visible;
    }

    Object.entries(this.visibilityMap).forEach(([name, visible]) => {
      this.page.toggleColumn(name, visible);
    });
  }

  private updateVisibilityMap(visibilityMap: VisibilityMap): void {
    this.preferenceStorageService.updatePreference(this.storageId, visibilityMap, true);
  }
}
